import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Contractor,
  Contract,
  getContractors,
  getContracts,
} from '../services/api';
import { SortState, compareValues, formatContractSumDisplay, toggleSort } from '../utils/table';
import { useAuth } from '../context/AuthContext';
import { SheetsColumnMultiFilter } from '../components/SheetsColumnMultiFilter';
import '../styles/DataPage.css';

function parseContractorsSumBound(raw: string): number | null {
  const t = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

type ContractorSortKey =
  | 'id'
  | 'name'
  | 'contractor_type'
  | 'address'
  | 'inn'
  | 'contracts_total';

const ContractorsPage: React.FC = () => {
  const navigate = useNavigate();
  const { can, hasDepartment } = useAuth();
  const canOpenPage = hasDepartment('ALL_COMP');
  const canContractsView = can('CONTRACTS', 'VIEW');
  const canSum = can('CONTRACT_SUM', 'VIEW');
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterMenuOpen, setFilterMenuOpen] = useState<'name' | 'inn' | null>(null);

  const [searchText, setSearchText] = useState('');
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const [filterInns, setFilterInns] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<'all' | 'ФЛ' | 'ЮЛ'>('all');
  const [contractSumFrom, setContractSumFrom] = useState('');
  const [contractSumTo, setContractSumTo] = useState('');

  const [sortState, setSortState] = useState<SortState<ContractorSortKey>>({
    key: 'name',
    direction: 'asc',
  });

  const contractSumByContractorId = useMemo(() => {
    const map = new Map<number, number>();
    if (!canContractsView || !canSum) {
      return map;
    }
    for (const contract of contracts) {
      const amount = Number(contract.total_amount) || 0;
      const prev = map.get(contract.contractor_id) ?? 0;
      map.set(contract.contractor_id, prev + amount);
    }
    return map;
  }, [contracts, canContractsView, canSum]);

  useEffect(() => {
    if (!filtersOpen) {
      setFilterMenuOpen(null);
    }
  }, [filtersOpen]);

  const fetchContractors = useCallback(async (): Promise<void> => {
    if (!canOpenPage) {
      setContractors([]);
      setContracts([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const contractorsPromise = getContractors();
      const contractsPromise = canContractsView
        ? getContracts().catch(() => [] as Contract[])
        : Promise.resolve([] as Contract[]);

      const [data, contractRows] = await Promise.all([contractorsPromise, contractsPromise]);
      setContractors(data);
      setContracts(contractRows);
    } catch (err) {
      setError('Ошибка при загрузке контрагентов');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [canOpenPage, canContractsView]);

  useEffect(() => {
    if (canOpenPage) {
      void fetchContractors();
    } else {
      setIsLoading(false);
      setContractors([]);
      setContracts([]);
    }
  }, [canOpenPage, canContractsView, fetchContractors]);

  const clearFilters = (): void => {
    setSearchText('');
    setFilterNames([]);
    setFilterInns([]);
    setTypeFilter('all');
    setContractSumFrom('');
    setContractSumTo('');
    setFilterMenuOpen(null);
  };

  /** Только поля боковой панели фильтров (поиск в тулбаре всегда виден). */
  const hasActiveFilters =
    filterNames.length > 0 ||
    filterInns.length > 0 ||
    typeFilter !== 'all' ||
    contractSumFrom.trim() !== '' ||
    contractSumTo.trim() !== '';

  const nameOptions = useMemo(() => {
    const set = new Set(contractors.map((c) => c.name));
    return [...set].sort((a, b) => a.localeCompare(b, 'ru'));
  }, [contractors]);

  const innOptions = useMemo(() => {
    const set = new Set<string>();
    for (const c of contractors) {
      set.add(c.inn ?? '');
    }
    return [...set].sort((a, b) => {
      if (a === '') return 1;
      if (b === '') return -1;
      return a.localeCompare(b, 'ru', { numeric: true });
    });
  }, [contractors]);

  const formatInnFilterOption = useCallback((value: string) => (value === '' ? '— (нет ИНН)' : value), []);

  const filteredContractors = useMemo(() => {
    const text = searchText.trim().toLowerCase();
    const sumFromN = parseContractorsSumBound(contractSumFrom);
    const sumToN = parseContractorsSumBound(contractSumTo);

    return contractors.filter((contractor) => {
      const fullText = [
        contractor.name,
        contractor.address,
        contractor.phone,
        contractor.email,
        contractor.inn,
        contractor.passport_data,
        contractor.position_relation,
        contractor.contact_full_name,
        contractor.additional_contacts,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const matchesSearch = !text || fullText.includes(text);
      const matchesName = filterNames.length === 0 || filterNames.includes(contractor.name);
      const matchesType = typeFilter === 'all' || contractor.contractor_type === typeFilter;
      const innKey = contractor.inn ?? '';
      const matchesInn = filterInns.length === 0 || filterInns.includes(innKey);

      const sum = contractSumByContractorId.get(contractor.id) ?? 0;
      const matchesSum =
        !canContractsView ||
        ((sumFromN === null || sum >= sumFromN) && (sumToN === null || sum <= sumToN));

      return matchesSearch && matchesName && matchesType && matchesInn && matchesSum;
    });
  }, [
    contractors,
    searchText,
    filterNames,
    filterInns,
    typeFilter,
    contractSumFrom,
    contractSumTo,
    contractSumByContractorId,
    canContractsView,
  ]);

  const sortedContractors = useMemo(() => {
    const sorted = [...filteredContractors];
    sorted.sort((left, right) => {
      if (sortState.key === 'contracts_total') {
        const leftValue = contractSumByContractorId.get(left.id) ?? 0;
        const rightValue = contractSumByContractorId.get(right.id) ?? 0;
        return compareValues(leftValue, rightValue, sortState.direction);
      }

      const leftValue = left[sortState.key as keyof Contractor] as string | number | undefined;
      const rightValue = right[sortState.key as keyof Contractor] as string | number | undefined;
      return compareValues(leftValue, rightValue, sortState.direction);
    });
    return sorted;
  }, [filteredContractors, sortState, contractSumByContractorId]);

  const totalFilteredContractsSum = useMemo(
    () =>
      sortedContractors.reduce((sum, contractor) => sum + (contractSumByContractorId.get(contractor.id) ?? 0), 0),
    [sortedContractors, contractSumByContractorId],
  );

  const handleSort = (key: ContractorSortKey): void => {
    setSortState((current) => toggleSort(current, key));
  };

  return (
    <div className="data-page contractors-page">
      {!canOpenPage ? (
        <div className="empty-state">Нет доступа к странице контрагентов (требуется отдел ALL_COMP).</div>
      ) : (
        <>
          <div className="page-header-row">
            <h1>Контрагенты</h1>
            <div className="page-header-row__actions">
              <button
                type="button"
                className={`filters-toggle ${!filtersOpen && hasActiveFilters ? 'filters-toggle--show-active-dot' : ''}`}
                onClick={() => setFiltersOpen((open) => !open)}
                aria-expanded={filtersOpen}
                aria-label={filtersOpen ? 'Скрыть фильтры' : 'Показать фильтры'}
              >
                Фильтры
                <span className="filters-toggle-badge" aria-hidden />
              </button>
            </div>
            <div className="page-header-row__search">
              <input
                type="text"
                className="search-input"
                placeholder="Поиск по текстовым полям"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className={`table-layout ${filtersOpen ? '' : 'table-layout--filters-collapsed'}`}>
            {filtersOpen && (
              <aside className="filters-panel">
                <div className="filters-header">
                  <h2>Фильтры</h2>
                  <button type="button" className="link-button" onClick={clearFilters}>
                    Сбросить
                  </button>
                </div>

                <div className="filters-grid">
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="Название"
                      options={nameOptions}
                      selected={filterNames}
                      onSelectedChange={setFilterNames}
                      formatOption={(v) => v}
                      isOpen={filterMenuOpen === 'name'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'name' : null)}
                    />
                  </div>
                  <div className="filters-field">
                    <SheetsColumnMultiFilter
                      label="ИНН"
                      options={innOptions}
                      selected={filterInns}
                      onSelectedChange={setFilterInns}
                      formatOption={formatInnFilterOption}
                      isOpen={filterMenuOpen === 'inn'}
                      onOpenChange={(open) => setFilterMenuOpen(open ? 'inn' : null)}
                    />
                  </div>
                  <label>
                    Тип
                    <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value as 'all' | 'ФЛ' | 'ЮЛ')}>
                      <option value="all">Все</option>
                      <option value="ФЛ">ФЛ</option>
                      <option value="ЮЛ">ЮЛ</option>
                    </select>
                  </label>
                  <label className="filters-sum-block">
                    <span className="filters-sum-block__title">Сумма договоров</span>
                    <div className="filters-sum-block__row">
                      <input
                        type="text"
                        inputMode="decimal"
                        className="filters-sum-block__input"
                        placeholder="от"
                        value={contractSumFrom}
                        onChange={(e) => setContractSumFrom(e.target.value)}
                        disabled={!canContractsView}
                        title={!canContractsView ? 'Нужен доступ к модулю «Договоры»' : undefined}
                      />
                      <input
                        type="text"
                        inputMode="decimal"
                        className="filters-sum-block__input"
                        placeholder="до"
                        value={contractSumTo}
                        onChange={(e) => setContractSumTo(e.target.value)}
                        disabled={!canContractsView}
                        title={!canContractsView ? 'Нужен доступ к модулю «Договоры»' : undefined}
                      />
                    </div>
                  </label>
                </div>
              </aside>
            )}

            <section className="table-content">
              {isLoading ? (
                <div className="loading">Загрузка...</div>
              ) : (
                <div className="table-panel">
                  <div className="table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleSort('id')} role="button" className="sortable">
                            ID
                          </th>
                          <th onClick={() => handleSort('name')} role="button" className="sortable">
                            Название
                          </th>
                          <th onClick={() => handleSort('contractor_type')} role="button" className="sortable">
                            Тип
                          </th>
                          <th onClick={() => handleSort('address')} role="button" className="sortable">
                            Юридический адрес
                          </th>
                          <th onClick={() => handleSort('inn')} role="button" className="sortable">
                            ИНН
                          </th>
                          <th onClick={() => handleSort('contracts_total')} role="button" className="sortable col-num">
                            Сумма договоров
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedContractors.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="data-table__empty">
                              Ничего не найдено
                            </td>
                          </tr>
                        ) : (
                          sortedContractors.map((contractor) => (
                            <tr
                              key={contractor.id}
                              className="clickable-row"
                              onClick={() => navigate(`/contractors/${contractor.id}`)}
                            >
                              <td>{contractor.id}</td>
                              <td>
                                <strong>{contractor.name}</strong>
                              </td>
                              <td>{contractor.contractor_type}</td>
                              <td>{contractor.address || '-'}</td>
                              <td>{contractor.inn || '-'}</td>
                              <td className="col-num">
                                {canContractsView
                                  ? formatContractSumDisplay(contractSumByContractorId.get(contractor.id) ?? 0)
                                  : '—'}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                  <div className="table-panel__footer">
                    <table className="data-table data-table--footer">
                      <tfoot>
                        <tr>
                          <td>Итого</td>
                          <td>{sortedContractors.length}</td>
                          <td />
                          <td />
                          <td />
                          <td className="col-num">
                            {canContractsView ? formatContractSumDisplay(totalFilteredContractsSum) : '—'}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </div>
  );
};

export default ContractorsPage;
